import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Snackbar,
  Paper,
  SelectChangeEvent,
} from '@mui/material';
import { ArrowBack, Add, Save, Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { promoCodesService } from './promo-codes.service';
import { CreatePromoCodeDto } from './promo-codes.dto';

const AddPromoCode = () => {
  console.log('AddPromoCode');
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [formData, setFormData] = useState<CreatePromoCodeDto>({
    promoCode: '',
    description: '',
    discountType: 'percentage',
    discountValue: 0,
    minimumOrderAmount: 0,
    maximumDiscount: 0,
    usageLimit: 0,
    startDate: '',
    endDate: '',
    status: 1, // 1 = Active, 0 = Inactive
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  const statusOptions = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'Inactive' },
  ];

  const typeOptions = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'fixed', label: 'Fixed Amount' },
  ];

  useEffect(() => {
    promoCodesService.setLogoutCallback(logout);
  }, [token, logout]);

  const handleChange =
    (field: keyof CreatePromoCodeDto) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      const target = event.target as HTMLInputElement | HTMLSelectElement;
      let value: string | number | string[] | 0 = target.value;

      if (field === 'status') {
        value = value === '' ? 1 : Number(value);
      } else if (field === 'discountValue') {
        value = value === '' ? 0 : Number(value);
      } else if (
        field === 'minimumOrderAmount' ||
        field === 'maximumDiscount' ||
        field === 'usageLimit'
      ) {
        // For optional fields, keep as undefined if empty, otherwise convert to number
        value = value === '' ? 0 : Number(value);
      }

      setFormData({ ...formData, [field]: value as any });
      if (errors[field as string]) {
        setErrors({ ...errors, [field as string]: '' });
      }
    };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.promoCode || !formData.promoCode.trim()) {
      newErrors.promoCode = 'Promo code is required';
    }

    if (!formData.discountType) {
      newErrors.discountType = 'Discount type is required';
    }

    if (!formData.discountValue || formData.discountValue <= 0) {
      newErrors.discountValue = 'Valid discount value is required';
    }

    if (formData.discountType === 'percentage' && formData.discountValue > 99) {
      newErrors.discountValue = 'Percentage cannot exceed 99%';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    if (formData.usageLimit && formData.usageLimit < 0) {
      newErrors.usageLimit = 'Usage limit cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !token) return;

    setLoading(true);
    try {
      // Clean form data - ensure all numeric fields are proper numbers
      const cleanedData: CreatePromoCodeDto = {
        promoCode: formData.promoCode.trim(),
        description: formData.description?.trim() || '',
        discountType: formData.discountType,
        discountValue: Number(formData.discountValue ?? 0),
        minimumOrderAmount: Number(formData.minimumOrderAmount ?? 0),
        maximumDiscount: Number(formData.maximumDiscount ?? 0),
        usageLimit: Number(formData.usageLimit ?? 0),
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status,
      };

      console.log('Submitting promo code data:', cleanedData);
      await promoCodesService.createPromoCode(token, cleanedData);

      setSnackbar({
        open: true,
        message: 'Promo code created successfully!',
        severity: 'success',
      });

      setTimeout(() => {
        navigate('/promo-codes');
      }, 1500);
    } catch (err) {
      console.error('Error creating promo code:', err);
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : 'Failed to create promo code',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/promo-codes');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton onClick={() => navigate('/promo-codes')} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Add color="primary" sx={{ mr: 1 }} />
            <Typography variant="h4" component="h1">
              Add New Promo Code
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Create a new promotional code with discount and usage controls
          </Typography>
        </Paper>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Left Sidebar - Status */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Promo Code Settings
                  </Typography>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={String(formData.status)}
                      label="Status"
                      onChange={handleChange('status')}
                    >
                      {statusOptions.map(option => (
                        <MenuItem
                          key={String(option.value)}
                          value={String(option.value)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>

            {/* Main Form Content */}
            <Grid item xs={12} md={8}>
              {/* Basic Information */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Promo Code"
                        placeholder="e.g., WELCOME20"
                        value={formData.promoCode}
                        onChange={handleChange('promoCode')}
                        error={!!errors.promoCode}
                        helperText={errors.promoCode}
                        required
                        inputProps={{
                          style: { textTransform: 'uppercase' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Discount Type</InputLabel>
                        <Select
                          value={formData.discountType}
                          label="Discount Type"
                          onChange={handleChange('discountType')}
                          error={!!errors.discountType}
                        >
                          {typeOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        placeholder="Brief description of the promo code"
                        value={formData.description}
                        onChange={handleChange('description')}
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={
                          formData.discountType === 'percentage'
                            ? 'Discount Percentage'
                            : 'Discount Amount'
                        }
                        type="number"
                        value={formData.discountValue}
                        onChange={handleChange('discountValue')}
                        error={!!errors.discountValue}
                        helperText={errors.discountValue}
                        required
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>
                              {formData.discountType === 'percentage'
                                ? '%'
                                : '$'}
                            </Typography>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Validity Period */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Validity Period
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={formData.startDate}
                        onChange={handleChange('startDate')}
                        error={!!errors.startDate}
                        helperText={errors.startDate}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={formData.endDate}
                        onChange={handleChange('endDate')}
                        error={!!errors.endDate}
                        helperText={errors.endDate}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Discount Rules */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Discount Rules
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Minimum Order Amount"
                        type="number"
                        value={formData.minimumOrderAmount}
                        onChange={handleChange('minimumOrderAmount')}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>$</Typography>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Maximum Discount"
                        type="number"
                        value={formData.maximumDiscount}
                        onChange={handleChange('maximumDiscount')}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>$</Typography>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Usage Limit"
                        type="number"
                        value={formData.usageLimit}
                        onChange={handleChange('usageLimit')}
                        error={!!errors.usageLimit}
                        helperText={
                          errors.usageLimit || 'Leave 0 for unlimited'
                        }
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Promo Code'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default AddPromoCode;
